
import Vue, { PropType } from 'vue';
import _debounce from 'lodash/debounce';
import { auth } from '@/firebase';
import { ListFetchResult } from '@/utils/cursorWalk';
import { TEAMS_API_BASE_URL, TeamMemberListRow, fetchTeamMembersPage } from '@/utils/teamsApi';
import { rememberMemberLabels, rememberedMemberLabels } from '@/components/Filters/teamMemberPickerLabels';

interface PickerItem {
	text: string;
	value: string;
}

// How many matches the list offers at a time. Typing narrows it on the server.
const PICKER_PAGE_SIZE = 50;

/**
 * A Filters box field for picking people on one Team, by their user id: Boards'
 * Owner. It searches the Team's joined members on the server as you type
 * (`/teams/{teamId}/members`), so a Team of thousands is as quick as a Team of
 * ten, rather than loading everyone up front as the old picker did. A chosen
 * person keeps their address on screen even when the current search does not
 * list them, and when the Filters box rebuilds this field: every address found
 * is kept for the session (teamMemberPickerLabels.ts). `knownLabels` names
 * anyone else the page already knows (the owners of the rows on screen).
 */
export default Vue.extend({
	name: 'TeamMemberPickerField',
	props: {
		value: { type: Array as PropType<string[]>, default: () => [] },
		teamId: { type: String, default: null },
		companyId: { type: String, default: null },
		// The account the request acts as, which is the Team itself for its own members
		accountTeamId: { type: String, default: null },
		// user id -> address, for people already chosen
		knownLabels: { type: Object as PropType<Record<string, string>>, default: () => ({}) },
		placeholder: { type: String, default: 'Owner' },
		icon: { type: String, default: 'mdi-account-outline' },
	},
	data () {
		return {
			search: null as string | null,
			fetched: [] as PickerItem[],
			// Every address found this session for the Team, not only by this instance
			labelCache: rememberedMemberLabels(this.teamId) as Record<string, string>,
			loading: false,
			loadedTeamId: null as string | null,
			requestVersion: 0,
			debouncedFetch: null as ((q: string) => void) | null,
		};
	},
	computed: {
		// The chosen people first, named as well as anyone can, then the matches
		items (): PickerItem[] {
			const seen: Record<string, true> = {};
			const out: PickerItem[] = [];
			(this.value || []).forEach((userId: string) => {
				if (seen[userId]) return;
				seen[userId] = true;
				out.push({ text: this.labelCache[userId] || this.knownLabels[userId] || 'Unknown member', value: userId });
			});
			this.fetched.forEach((item: PickerItem) => {
				if (seen[item.value]) return;
				seen[item.value] = true;
				out.push(item);
			});
			return out;
		},
		noDataText (): string {
			return this.loading ? 'Searching...' : 'No members match';
		},
	},
	watch: {
		search (newVal: string | null, oldVal: string | null) {
			if ((newVal || '') === (oldVal || '')) return;
			if (this.debouncedFetch) this.debouncedFetch(newVal || '');
		},
		teamId (newVal: string | null, oldVal: string | null) {
			if (newVal === oldVal) return;
			// Another Team's people are not this one's to offer, nor to name
			this.fetched = [];
			this.loadedTeamId = null;
			this.labelCache = rememberedMemberLabels(newVal);
		},
	},
	created () {
		this.debouncedFetch = _debounce((q: string) => this.fetchMatches(q), 300);
	},
	beforeDestroy () {
		const debounced = this.debouncedFetch as unknown as { cancel?: () => void } | null;
		if (debounced && debounced.cancel) debounced.cancel();
	},
	methods: {
		onInput (next: string[]) {
			this.$emit('input', Array.isArray(next) ? next : []);
		},
		// The first page waits until the field is used, so an unopened box costs nothing
		loadFirstPage () {
			if (this.loadedTeamId === this.teamId) return;
			this.fetchMatches(this.search || '');
		},
		async fetchMatches (q: string) {
			if (!this.teamId) return;
			const version: number = ++this.requestVersion;
			const teamId: string = this.teamId;
			this.loading = true;
			let result: ListFetchResult<TeamMemberListRow>;
			try {
				const currentUser = auth.currentUser;
				const idToken: string | null = currentUser ? await currentUser.getIdToken() : null;
				result = idToken
					? await fetchTeamMembersPage({
						baseUrl: TEAMS_API_BASE_URL,
						idToken,
						request: {
							teamId,
							companyId: this.companyId,
							accountTeamId: this.accountTeamId,
							limit: PICKER_PAGE_SIZE,
							q: q || null,
							status: 'joined',
						},
					})
					: { ok: false, reason: 'unauthorized' };
			} catch (e) {
				console.error('Error searching members:', e);
				result = { ok: false, reason: 'failed' };
			}
			if (version !== this.requestVersion) return;
			this.loading = false;
			if (result.ok !== true) return;
			const rows: TeamMemberListRow[] = result.page.rows.filter((row: TeamMemberListRow) => !!row.userId);
			const found: Record<string, string> = {};
			rows.forEach((row: TeamMemberListRow) => {
				found[row.userId] = row.email;
			});
			// Kept past this field, which the Filters box rebuilds every time it opens
			rememberMemberLabels(teamId, found);
			// The Team changed while this was in flight: these are not its people
			if (teamId !== this.teamId) return;
			this.loadedTeamId = teamId;
			this.fetched = rows.map((row: TeamMemberListRow) => ({ text: row.email, value: row.userId }));
			this.labelCache = { ...this.labelCache, ...found };
		},
	},
});
